import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { FixLogo } from '../FixLogo'

export function Navbar() {
  const { user, signOut } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  function focusPostcode() {
    document.getElementById('emergency-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => {
      document.getElementById('postcode-input')?.focus()
    }, 400)
  }

  function handleEmergencyClick(e: React.MouseEvent) {
    e.preventDefault()
    setMenuOpen(false)
    if (location.pathname === '/') {
      focusPostcode()
    } else {
      navigate('/')
      setTimeout(() => {
        focusPostcode()
      }, 100)
    }
  }

  return (
    <>
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" style={{ height: '60px' }}>
        <Link to="/" className="flex items-center gap-2 group" onClick={() => setMenuOpen(false)}>
          <FixLogo size={28} className="text-gold-500" />
          <span className="font-bold text-gray-900 text-lg tracking-tight">FixDelivered</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-6">
          <button onClick={handleEmergencyClick} className="text-sm text-red-600 hover:text-red-700 transition-colors font-medium">
            Emergency Call-out
          </button>
          <Link to="/plumbers" className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">
            Plumbers
          </Link>
          <Link to="/blog" className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">
            Advice
          </Link>
          <Link to="/my-account" className="text-sm font-semibold text-gray-900 hover:text-gold-600 transition-colors cursor-pointer">
            My Account
          </Link>
          {user && (
            <button
              onClick={() => void signOut()}
              className="text-sm text-gray-400 hover:text-gray-900 transition-colors font-medium"
            >
              Sign out
            </button>
          )}
        </div>

        {/* Mobile burger */}
        <button
          type="button"
          className="sm:hidden p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen(o => !o)}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

    </nav>

    {/* Mobile full-screen drawer — portalled to body so it covers everything */}
    {menuOpen && createPortal(
      <div className="sm:hidden fixed inset-0 z-[100] flex flex-col bg-white animate-slide-down">
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 border-b border-gray-100" style={{ height: '60px' }}>
          <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            <FixLogo size={28} className="text-gold-500" />
            <span className="font-bold text-gray-900 text-lg tracking-tight">FixDelivered</span>
          </Link>
          <button
            type="button"
            className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          <button
            onClick={handleEmergencyClick}
            className="w-full text-left flex items-center gap-3 px-3 py-4 rounded-xl text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            Emergency Call-out
          </button>
          <Link
            to="/plumbers"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-4 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Plumbers
          </Link>
          <Link
            to="/blog"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-4 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Advice &amp; guides
          </Link>
          <div className="h-px bg-gray-100 my-2" />
          <Link
            to="/my-account"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-4 rounded-xl text-base font-semibold text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            My Account
          </Link>
          {user && (
            <button
              onClick={() => { void signOut(); setMenuOpen(false) }}
              className="w-full text-left flex items-center gap-3 px-3 py-4 rounded-xl text-base font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    , document.body)}
    </>
  )
}
